import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CarouselModule } from 'primeng/carousel';

import { WebsiteSection, HeroSlide } from '../../core/models/website.models';
import { ImageValidatorService } from '../../core/services/image-validator.service';

@Component({
  selector: 'app-dynamic-section',
  imports: [CommonModule, FormsModule, RouterLink, CarouselModule],
  templateUrl: './dynamic-section.html',
  styleUrl: './dynamic-section.scss',
})
export class DynamicSectionComponent {
  private _section!: WebsiteSection;

  @Input({ required: true })
  set section(section: WebsiteSection) {
    this._section = section;
    if (section?.imageUrl) {
      this.validateSectionImage();
    } else {
      this.isImageValid.set(false);
    }
  }

  get section(): WebsiteSection {
    return this._section;
  }

  @Output() sectionAction = new EventEmitter<string>();

  readonly formStatus = signal('');
  readonly openFaq = signal(0);
  readonly isImageValid = signal(false);

  constructor(private readonly imageValidator: ImageValidatorService) {}

  private validateSectionImage(): void {
    if (!this.section?.imageUrl) {
      this.isImageValid.set(false);
      return;
    }

    this.imageValidator
      .validateImageUrl(this.section.imageUrl)
      .then((isValid: boolean) => {
        this.isImageValid.set(isValid);
      })
      .catch(() => {
        this.isImageValid.set(false);
      });
  }

  submitContact(): void {
    this.formStatus.set('Thanks. Your message has been captured for the DySi team.');
  }

  ratingArray(rating = 5): number[] {
    return Array.from({ length: Math.max(0, rating) });
  }

  getOverlayOpacity(): number {
    const transparency = this.section?.bgTransparency !== undefined ? this.section.bgTransparency : 22;
    return (100 - transparency) / 100;
  }

  slideStyles(slide: HeroSlide): Record<string, string> {
    return {
      'background-image': `url('${slide.imageUrl}')`,
      'background-size': 'cover',
      'background-position': 'center',
      'background-repeat': 'no-repeat'
    };
  }

  sectionStyles(section: WebsiteSection): Record<string, string> {
    const styles: Record<string, string> = {
      'background-color': section.backgroundColor || '',
      color: section.textColor || '',
      'font-family': section.fontFamily || '',
      'font-size': section.fontSize || '',
    };

    // Calculate transparency for overlay
    const transparency = section.bgTransparency !== undefined ? section.bgTransparency : 22;
    const opacity = (100 - transparency) / 100;
    styles['--overlay-opacity'] = String(opacity);

    // Apply background image if valid
    if (this.isImageValid() && section.imageUrl) {
      styles['background-image'] = `url('${section.imageUrl}')`;
      styles['background-size'] = 'cover';
      styles['background-position'] = 'center';
      styles['background-repeat'] = 'no-repeat';
      styles['transition'] = 'background-image 0.3s ease-in-out, background-color 0.3s ease-in-out';
    } else {
      styles['transition'] = 'background-image 0.3s ease-in-out, background-color 0.3s ease-in-out';
    }

    return styles;
  }
}
